import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, ROOMS } from "@/lib/api";
import TonightsPick from "@/components/TonightsPick";
import RoomArt from "@/components/RoomArt";

/**
 * Hall — landing screen after sign-in. Eight room panels arranged in a grid,
 * each hinting at its contents via a subtle texture. Beneath the rooms sits
 * today's pick strip and a quiet link to Collections + Settings.
 */
export default function Hall() {
  const [counts, setCounts] = useState({});
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const r = await api.get("/items");
        if (cancel) return;
        const list = r.data || [];
        const c = {};
        for (const it of list) c[it.type] = (c[it.type] || 0) + 1;
        setCounts(c);
        setItems(list);
      } catch (e) {
        // Hall still renders the empty rooms — log so debugging is easier.
        console.error("[hall] /items failed:", e);
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, []);

  return (
    <div className="max-w-6xl mx-auto px-6 sm:px-10 pt-14 pb-24" data-testid="hall">
      <header className="text-center max-w-2xl mx-auto">
        <p className="font-mono-lc text-[10px] tracking-[0.28em] uppercase text-[var(--brass)] opacity-80">
          curated ethos
        </p>
        <h1 className="mt-5 font-serif-lit text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-[var(--cream)]">
          A space built around what you love, not what an algorithm pushes.
        </h1>
        <p className="mt-4 font-serif-lit italic text-[var(--cream-dim)] text-lg">
          Your shelf stays yours. Share what you want, when you want.
        </p>
      </header>

      <section className="mt-14 grid grid-cols-2 sm:grid-cols-3 gap-4 sm:gap-5" data-testid="rooms-grid">
        {ROOMS.map(room => (
          <Link
            key={room.type}
            to={room.to || `/room/${room.type}`}
            data-testid={`room-${room.type}`}
            className="hall-panel"
          >
            <div className="hall-panel-art">
              <RoomArt type={room.type} />
            </div>
            <div className="hall-panel-inner">
              <p className="hall-panel-name">{room.name}</p>
              <p className="hall-panel-count">
                {room.social
                  ? room.copy
                  : loading
                    ? "…"
                    : (counts[room.type] || 0) === 1
                      ? room.one
                      : `${counts[room.type] || 0} ${room.copy}`}
              </p>
            </div>
          </Link>
        ))}
      </section>

      {!loading && items.length > 0 && (
        <div className="mt-16">
          <TonightsPick items={items} />
        </div>
      )}
    </div>
  );
}
